import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getUser } from '../services/auth';
import toast from '../services/toast';
import { compartilharFigurinha } from '../utils/canvasFigurinha';
import Icon from '../components/Icon';
import './Figurinha.css';

const APP_URL = process.env.REACT_APP_APP_URL || 'https://app.damelsoft.com.br';

function FormFigurinha({ opcoes, form, setForm, onSubmit, salvando, onCancelar }) {
  const jogadoresDoTime = opcoes.jogadores.filter(j => String(j.time_id) === String(form.time_id));
  return (
    <form className="card figurinha-form" onSubmit={onSubmit}>
      <div className="form-group">
        <label>Meu time</label>
        <select value={form.time_id} onChange={e => setForm({ ...form, time_id: e.target.value, jogador_id: '' })} required>
          <option value="">Selecione seu time</option>
          {opcoes.times.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Meu animal</label>
        <select value={form.animal_id} onChange={e => setForm({ ...form, animal_id: e.target.value })} required>
          <option value="">Selecione um animal</option>
          {opcoes.animais.map(a => <option key={a.id} value={a.id}>{a.emoji} {a.nome}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Meu craque (opcional)</label>
        <select value={form.jogador_id} onChange={e => setForm({ ...form, jogador_id: e.target.value })} disabled={!form.time_id}>
          <option value="">Nenhum jogador selecionado</option>
          {jogadoresDoTime.map(j => <option key={j.id} value={j.id}>{j.nome} - {j.time_nome}</option>)}
        </select>
      </div>
      <div className="figurinha-actions">
        <button type="submit" className="btn btn-primary btn-block" disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
        {onCancelar && <button type="button" className="btn btn-secondary btn-block" onClick={onCancelar}>Cancelar</button>}
      </div>
    </form>
  );
}

export default function Figurinha() {
  const user = getUser();
  const [dados, setDados] = useState(null);
  const [opcoes, setOpcoes] = useState(null);
  const [torcidas, setTorcidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({ time_id: '', animal_id: '', jogador_id: '' });
  const [compartilhando, setCompartilhando] = useState(false);

  function carregar() {
    setLoading(true);
    api.get('/figurinhas/minha').then(d => {
      setDados(d);
      if (d.temFigurinha) {
        api.get('/figurinhas/torcidas').then(setTorcidas).catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }

  useEffect(carregar, []);

  function iniciarCriacao() {
    setCriando(true);
    setForm({ time_id: '', animal_id: '', jogador_id: '' });
    if (!opcoes) api.get('/figurinhas/opcoes').then(setOpcoes).catch(() => {});
  }

  function iniciarEdicao() {
    setEditando(true);
    setForm({
      time_id: dados.figurinha.time_id,
      animal_id: dados.figurinha.animal_id,
      jogador_id: dados.figurinha.jogador_id || '',
    });
    if (!opcoes) api.get('/figurinhas/opcoes').then(setOpcoes).catch(() => {});
  }

  async function salvarCriacao(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.post('/figurinhas', {
        time_id: form.time_id, animal_id: form.animal_id, jogador_id: form.jogador_id || null,
      });
      toast.success('Figurinha Premium criada! +100 XP');
      setCriando(false);
      carregar();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSalvando(false);
    }
  }

  async function salvarEdicao(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.put('/figurinhas', {
        time_id: form.time_id, animal_id: form.animal_id, jogador_id: form.jogador_id || null,
      });
      toast.success('Figurinha atualizada!');
      setEditando(false);
      carregar();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSalvando(false);
    }
  }

  async function compartilhar() {
    setCompartilhando(true);
    try {
      const f = dados.figurinha;
      const ok = await compartilharFigurinha({
        nome: dados.usuario.nome,
        cidadeNome: dados.usuario.cidade_nome,
        animalEmoji: f.animal_emoji,
        timeNome: f.time_nome,
        torcedores: dados.torcedoresDoTime,
        jogadorNome: f.jogador_nome,
        jogadorTimeNome: f.jogador_time_nome,
        feitos: dados.palpites.feitos,
        acertados: dados.palpites.acertados,
        aproveitamento: dados.palpites.aproveitamento,
        nivelNome: dados.nivel.atual.nome,
        nivelEmoji: dados.nivel.atual.emoji,
        nivelCor: dados.nivel.atual.cor,
        colocacaoDoTime: dados.colocacaoDoTime,
        xp: f.xp,
        xpPercentual: dados.nivel.percentual,
        xpFaltam: dados.nivel.faltam,
        temProximoNivel: !!dados.nivel.proximo,
        nivelProximoNome: dados.nivel.proximo?.nome,
      });
      if (ok) {
        const r = await api.post('/figurinhas/compartilhar');
        toast.success(r.xpGanho > 0 ? `Compartilhado! +${r.xpGanho} XP` : 'Compartilhado!');
      }
    } catch (err) {
      toast.error('Não foi possível gerar a imagem da figurinha.');
    } finally {
      setCompartilhando(false);
    }
  }

  async function convidarAmigo() {
    const link = `${APP_URL}/cadastro?ref=${dados.usuario.id}`;
    const text = `⚽ Venha participar do Palpitar comigo! Cadastre-se pelo meu link: ${link}`;
    if (navigator.share) {
      try { await navigator.share({ text }); } catch (_) {}
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link de convite copiado!');
    } catch (_) {
      toast.error('Não foi possível copiar o link.');
    }
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  if (!dados?.temFigurinha) {
    return (
      <div className="figurinha-page">
        <h1 className="page-title"><Icon name="trophy" size={20} /> Figurinha do Torcedor Premium</h1>
        {!criando ? (
          <div className="card figurinha-vazia">
            <span className="figurinha-vazia-icone"><Icon name="trophy" size={30} /></span>
            <p>Você ainda não criou sua Figurinha Premium.</p>
            <button className="btn btn-primary btn-block" onClick={iniciarCriacao}>Criar minha Figurinha Premium</button>
          </div>
        ) : opcoes ? (
          <FormFigurinha opcoes={opcoes} form={form} setForm={setForm} onSubmit={salvarCriacao} salvando={salvando} onCancelar={() => setCriando(false)} />
        ) : (
          <div className="loading"><div className="spinner" /></div>
        )}
      </div>
    );
  }

  const { figurinha: f, nivel, palpites } = dados;

  return (
    <div className="figurinha-page">
      <h1 className="page-title"><Icon name="trophy" size={20} /> Figurinha do Torcedor Premium</h1>

      {editando && opcoes ? (
        <FormFigurinha opcoes={opcoes} form={form} setForm={setForm} onSubmit={salvarEdicao} salvando={salvando} onCancelar={() => setEditando(false)} />
      ) : (
        <>
        <div className={`fcard nivel-${nivel.atual.cor}`}>
          <div className="fcard__bg">
            <div className="fcard__glow" />
            {nivel.atual.cor === 'dourado' && <div className="fcard__rays" />}
            <span className="fcard__spark s1" /><span className="fcard__spark s2" /><span className="fcard__spark s3" />
          </div>

          <div className="fcard__ribbon"><Icon name="trophy" size={12} /> {nivel.atual.nome}</div>

          <div className="fcard__mascote">
            <div className="fcard__mascote-ring"><div className="fcard__mascote-face">{f.animal_emoji}</div></div>
          </div>

          <h2 className="fcard__nome">{dados.usuario.nome}</h2>

          <div className="fcard__time">
            <span className="fcard__time-eyebrow">Torcedor do</span>
            <span className="fcard__time-nome">{f.time_nome}</span>
          </div>
          <p className="fcard__cidade"><Icon name="pin" size={12} /> {dados.usuario.cidade_nome}</p>

          <div className="fcard__torcida">
            <Icon name="users" size={16} />
            <div className="fcard__torcida-txt">
              <b>{dados.torcedoresDoTime.toLocaleString('pt-BR')} torcedor{dados.torcedoresDoTime === 1 ? '' : 'es'} do {f.time_nome}</b>
              {dados.colocacaoDoTime && (
                <span><Icon name="crown" size={12} /> {dados.colocacaoDoTime}ª maior torcida de {dados.usuario.cidade_nome}</span>
              )}
            </div>
          </div>

          {f.jogador_nome && (
            <div className="fcard__craque">
              <span className="fcard__craque-eyebrow"><Icon name="star" size={12} /> Meu craque</span>
              <b>{f.jogador_nome}</b>
              <small>{f.jogador_time_nome}</small>
            </div>
          )}

          <div className="fcard__stats">
            <div><Icon name="target" /><b>{palpites.feitos}</b><small>palpites</small></div>
            <div><Icon name="check" /><b>{palpites.acertados}</b><small>acertos</small></div>
            <div><Icon name="fire" /><b>{palpites.aproveitamento.toString().replace('.', ',')}%</b><small>precisão</small></div>
          </div>

          <div className="fcard__xp">
            <div className="fcard__xp-top">
              <span>{f.xp} XP</span>
              {nivel.proximo && <span>Faltam {nivel.faltam} XP</span>}
            </div>
            <div className="fcard__xp-track">
              <div className="fcard__xp-fill" style={{ width: `${nivel.percentual}%` }}><span className="fcard__xp-head" /></div>
            </div>
            {nivel.proximo && <span className="fcard__xp-next">Próximo nível: {nivel.proximo.nome}</span>}
          </div>

          <div className="fcard__footer"><Icon name="bolt" size={12} /> Orgulho do Futebol Regional <b>palpitar</b></div>
        </div>

        <div className="figurinha-actions" style={{ marginTop: 16 }}>
          <button className="btn btn-secondary btn-block" onClick={iniciarEdicao}><Icon name="edit" size={13} /> Editar</button>
          <button className="btn btn-gold btn-block" onClick={compartilhar} disabled={compartilhando}>
            <Icon name="share" size={13} /> {compartilhando ? 'Gerando imagem...' : 'Compartilhar'}
          </button>
        </div>
        <button className="btn btn-secondary btn-block" style={{ marginTop: 10 }} onClick={convidarAmigo}>
          Convidar amigos (+150 XP ao se cadastrarem)
        </button>
        </>
      )}

      <section className="home-section">
        <div className="section-header">
          <h2><Icon name="trophy" size={14} /> Quem tem a maior torcida?</h2>
          <Link to="/craques">Craque mais votado</Link>
        </div>
        {torcidas.length === 0 ? <p className="empty">Nenhuma torcida cadastrada ainda.</p> : torcidas.slice(0, 10).map(t => (
          <div key={t.id} className={`ranking-item${t.posicao <= 3 ? ` top${t.posicao}` : ''}`}>
            <span className="rank-pos">{t.posicao === 1 ? '🥇' : t.posicao === 2 ? '🥈' : t.posicao === 3 ? '🥉' : `#${t.posicao}`}</span>
            <div className="rank-info">
              <span className="rank-nome">{t.nome}</span>
            </div>
            <span className="rank-pts">{t.torcedores.toLocaleString('pt-BR')} torcedores</span>
          </div>
        ))}
      </section>
    </div>
  );
}
